import { auth } from "@/lib/firebase"
import { useQuery } from "@tanstack/react-query"

// 1. Define TypeScript types matching the backend response
type StageStatus = "not_started" | "in_progress" | "completed"

interface Stage1Data {
	status: StageStatus
	understood_file_type?: string | null
	progress_index?: number | null
	progress_total?: number | null
}

interface Stage2Data {
	status: StageStatus
	full_summary?: string | null
	one_line_summary?: string | null
	two_line_summary?: string | null
	four_line_summary?: string | null
}

interface Stage3Data {
	status: StageStatus
	is_relevant?: boolean | null
	relevance_reasons?: string[] | null
	irrelevance_reasons?: string[] | null
}

export interface DocumentStagesData {
	stage1: Stage1Data
	stage2: Stage2Data
	stage3: Stage3Data
}

// 2. Define the fetch function using native fetch
export const fetchDocumentStages = async (
	caseId: string,
	documentId: string
): Promise<DocumentStagesData> => {
	const user = auth.currentUser
	if (!user) {
		throw new Error("User not found.")
	}
	const token = await user.getIdToken()
	const apiUrl = `${
		import.meta.env.VITE_API_URL
	}/api/cases/${caseId}/documents/${documentId}/stages/` // Assuming API is served from the same origin

	const response = await fetch(apiUrl, {
		headers: { Authorization: `Bearer ${token}` },
	})

	if (!response.ok) {
		const errorData = await response.text() // Try to get error details
		throw new Error(
			`Failed to fetch document stages: ${response.status} ${response.statusText} - ${errorData}`
		)
	}

	const data: DocumentStagesData = await response.json()
	return data
}

// 3. Create the custom hook using useQuery
export const useDocumentStages = (caseId?: string, documentId?: string) => {
	return useQuery<DocumentStagesData, Error>({
		queryKey: ["documentStages", caseId, documentId], // Unique key for the query
		queryFn: () => {
			if (!caseId || !documentId) {
				return Promise.reject(new Error("Case ID and Document ID are required"))
			}
			return fetchDocumentStages(caseId, documentId)
		},
		enabled: !!caseId && !!documentId, // Only run the query if IDs are provided
		staleTime: 5 * 60 * 1000, // Optional: Data is considered fresh for 5 minutes
		refetchOnWindowFocus: false, // Optional: Prevent refetching on window focus
		refetchInterval: 5000, // Refresh every 5 seconds
	})
}
